import { useMemo, useCallback } from "react"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import RiskSummaryChart from "./summary-chart"
import { createRiskMultiDateQuery, TOTAL_RISK, FACTORS_RISK, SPECIFIC_RISK } from "gatherers/multi-date/risk"
import { filterItemsWithData, extractDatesFromFirstSeries } from "connected-components/graph/helpers"
import { useBaseWithSegment, useFilteredSegment } from "connected-components/dashboard/helpers"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { ModelMetadata } from "resources/models"
import { ChartColors } from "connected-components/graph/types"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useIntradayState } from "connected-components/dashboard/hooks/use-intraday-state"
import type { ClickPosition } from "connected-components/dashboard/drilldown/types"
import { useGraphTemplateChart } from "../../hooks/use-graph-template-chart"
import { GRAPH_RISK_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS } from "../template-chart-config"
import { useGraphData } from "../../hooks/use-graph-data"

interface RiskGraphProps {
  modelMetadata: ModelMetadata
}

interface RiskItem {
  id: string
  label: string
  color: string
  category?: string
  disabled: boolean
  type: string
}

function createSummaryRiskItems(disabledItems: Record<string, boolean>, chartColors: ChartColors): RiskItem[] {
  return [
    {
      id: TOTAL_RISK,
      label: "Total Risk",
      color: chartColors.total,
      disabled: Boolean(disabledItems[TOTAL_RISK]),
      type: "overall",
    },
    {
      id: FACTORS_RISK,
      label: "Factor Risk",
      color: chartColors.factor,
      disabled: Boolean(disabledItems[FACTORS_RISK]),
      type: "overall",
    },
    {
      id: SPECIFIC_RISK,
      label: "Specific Risk",
      color: chartColors.alpha,
      disabled: Boolean(disabledItems[SPECIFIC_RISK]),
      type: "overall",
    },
  ]
}

function createCategoryFactorRiskItems(
  modelMetadata: ModelMetadata,
  selectedCategory: string,
  disabledItems: Record<string, boolean>,
): RiskItem[] {
  if (!modelMetadata?.factors) return []

  return modelMetadata.factors?.filter(factor => factor.category === selectedCategory)
    .map(factor => ({
      id: factor.id,
      label: factor.label,
      category: factor.category,
      disabled: Boolean(disabledItems[factor.id]),
      type: "factor",
      color: "",
    }))
}

export default function RiskGraph({ modelMetadata }: RiskGraphProps) {
  const {
    startDate,
    endDate,
    base,
    filter,
    normalization,
    instrument,
    comparisonInstrument,
    secondInstrument,
    isEndVsStartDateComparison,
    disabledItems,
    setDisabledItems,
    setOnlyItem,
    enableItems,
    intradayBatchMetadata,
    intradayPricingAsOf,
  } = useGraphData()

  const { selectedCategory } = useGraphTemplateChart(GRAPH_RISK_TEMPLATE_KEY, GRAPH_CATEGORY_DEFAULTS)
  const [{ isIntradayMode }] = useIntradayState()

  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()

  const handleDotClick = useCallback((clickPosition: ClickPosition | undefined, focusedItemId: string | null | undefined) => {
    if (!panelId || !focusedItemId) return
    const snapshotDate = clickPosition?.date ?? endDate
    // Summary chart lines ("risk-total", "risk-factors", "risk-specific") are internal IDs,
    // not model factor IDs. Only forward focusedItemId as selectedFactor in the factor view.
    const selectedFactor = selectedCategory !== "total" ? focusedItemId : undefined
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "graph.risk",
      title: "Asset View: Risk",
      view: "risk",
      context: {
        modelId: modelMetadata.id,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base,
        startDate,
        endDate: snapshotDate,
        isIntradayMode,
        ...resolveIntradayDrilldownFields(isIntradayMode, intradayBatchMetadata, intradayPricingAsOf),
        filter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: {
        selectedFactor,
        selectedCategory: selectedCategory === "total" ? "factors" : selectedCategory,
        clickedDate: snapshotDate,
        frozenSnapshot: { endDate: snapshotDate },
      },
    }))
  }, [panelId, openDrilldown, selectedCategory, modelMetadata.id, instrument, comparisonInstrument, secondInstrument, base, startDate, endDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, filter, normalization, isEndVsStartDateComparison])

  const baseWithSegment = useBaseWithSegment(base ?? null, filter, normalization)
  const segment = useFilteredSegment(filter, normalization)
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const chartColors = useThemeChartColors() as ChartColors
  const currentItems = useMemo(() => {
    if (selectedCategory === "total") {
      return createSummaryRiskItems(disabledItems, chartColors)
    }
    return createCategoryFactorRiskItems(modelMetadata, selectedCategory, disabledItems)
  }, [modelMetadata, selectedCategory, disabledItems, chartColors])

  const filterItems = useCallback(
    (items: RiskItem[], data: unknown[]) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const gatherers = useMemo(() => [
    // TODO: [INTRADAY-BATCH] replace with intraday batch data when isIntradayEnabled
    createRiskMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      startDate,
      endDate,
    ),
  ], [modelMetadata, instrumentWithSegment, baseWithSegment, startDate, endDate])

  const Renderer = selectedCategory === "total" ? RiskSummaryChart : MultiLineGraph

  return <DataIntegrator
    dataTransform={extractDatesFromFirstSeries}
    metrics={gatherers}
    Renderer={Renderer}
    rendererProps={{
      items: currentItems,
      filterItems: selectedCategory === "total" ? undefined : filterItems,
      endDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableItems,
      onDotClick: handleDotClick,
      loadingMessage: "Loading risk data...",
      errorMessage: "Failed to load risk data. Please try again.",
      emptyMessage: "No risk data available.",
    }}
  />
}
